import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import { Reveal, Overline } from "@/components/Reveal";
import { useSEO } from "@/lib/useSEO";
import { NEWSLETTER_SERIES, getIssue, issuePath, readingTime, sortedNewsletters } from "@/lib/newsletters";

export default function NewsletterArchive() {
  useSEO({
    fullTitle: `${NEWSLETTER_SERIES} — Tous les numéros | Observatoire HERUS`,
    title: NEWSLETTER_SERIES,
    description: "Archive de la newsletter HERUS Axiom consacrée à la structuration, à la gouvernance et à l'impact des projets d'infrastructures.",
    path: "/observatoire/newsletter",
  });
  const issues = sortedNewsletters();

  return (
    <div data-testid="newsletter-archive-page" className="bg-[#F9F6F0]">
      <section className="pt-40 lg:pt-52 pb-16 mx-auto max-w-[1600px] px-6 lg:px-12">
        <Link to="/observatoire" data-testid="archive-back-link" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#0A0A0A]/60 hover:text-[#C5A059] transition-colors">
          <ArrowLeft size={14} /> Observatoire HERUS
        </Link>
        <div className="mt-10"><Overline>Newsletter · Archive</Overline></div>
        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, ease: [0.19, 1, 0.22, 1] }}
          className="mt-8 font-serif-display font-light leading-[0.95] tracking-tighter text-[#0A0A0A] text-5xl sm:text-6xl lg:text-7xl max-w-5xl"
        >
          Les infrastructures <span className="italic text-[#C5A059]">à l'épreuve des faits.</span>
        </motion.h1>
        <p className="mt-8 max-w-2xl text-[#0A0A0A]/70 text-base lg:text-lg font-light leading-relaxed">
          Tous les numéros de la newsletter HERUS Axiom, du plus récent au plus ancien.
        </p>
      </section>

      <section data-testid="archive-list" className="mx-auto max-w-[1600px] px-6 lg:px-12 pb-24">
        {issues.map((n, i) => (
          <Reveal key={n.slug} i={i}>
            <Link
              to={`/observatoire/${n.slug}`}
              data-testid={`archive-item-${n.slug}`}
              className="group grid grid-cols-12 gap-6 items-center border-t border-[#0A0A0A]/10 py-10 last:border-b"
            >
              <span className="col-span-12 md:col-span-2 font-serif-display text-4xl lg:text-5xl font-light text-[#C5A059]">{n.number}</span>
              <div className="col-span-12 md:col-span-3 overflow-hidden aspect-video">
                <img src={n.cover} alt={n.title} className="w-full h-full object-cover transition-transform duration-[1.2s] group-hover:scale-105" />
              </div>
              <div className="col-span-12 md:col-span-6">
                <div className="flex flex-wrap gap-x-5 text-xs uppercase tracking-[0.2em] text-[#0A0A0A]/50">
                  <span className="text-[#C5A059]">{n.category}</span>
                  <span>{n.date}</span>
                  <span>{readingTime(n)} min de lecture</span>
                </div>
                <h2 className="mt-4 font-serif-display text-2xl lg:text-3xl font-light text-[#0A0A0A] leading-snug group-hover:text-[#C5A059] transition-colors duration-500">{n.title}</h2>
                <p className="mt-3 text-[#0A0A0A]/60 font-light leading-relaxed">{n.excerpt}</p>
              </div>
              <ArrowUpRight size={28} className="hidden md:block col-span-1 justify-self-end text-[#0A0A0A]/40 transition-all duration-500 group-hover:text-[#C5A059] group-hover:translate-x-1 group-hover:-translate-y-1" />
            </Link>
            {getIssue(n.slug, "en") && (
              <Link to={issuePath(n.slug, "en")} data-testid={`archive-item-en-${n.slug}`} className="mt-3 inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#0A0A0A]/60 hover:text-[#C5A059] transition-colors">
                English version <ArrowUpRight size={14} />
              </Link>
            )}
          </Reveal>
        ))}
      </section>
    </div>
  );
}
